/**
 * Turning one stored entry into the words a row shows: a name, a number, a
 * date, a unit.
 *
 * Pure, and tested without a device. Every rule here reads a binding, never a
 * business word: a sales row's title is whatever the record bound `title` to,
 * and the same function draws a clinic visit and a stock movement.
 *
 * Source: docs/plans/done/entry-flow-and-ui.md, Stage 2.
 */
import type {
  EntryListItem,
  EntryValue,
  EntryValues,
  PurposeId,
  RecordSchemaField,
  RecordSchemaResponse,
} from '@alluzo/shared';

import { formOpeningOf } from './form-sections';
import { t, tData } from './i18n';
import { dayInZone, workspaceZone } from './today-totals';

/** The field id one purpose points at, or null when it is answered another way. */
export const fieldFor = (schema: RecordSchemaResponse, purpose: PurposeId): string | null => {
  const binding = schema.bindings[purpose];
  return binding !== undefined && 'fieldId' in binding ? binding.fieldId : null;
};

/**
 * The money field the keypad opens on, or null when there is none.
 *
 * The focused input is the field the form opens on, and a record whose focused
 * input is an amount is a record the keypad should already be up on. That is
 * all this answers.
 *
 * It no longer decides which field is NATIVE. Every money field on the form
 * draws the native box and writes to a session of its own, keyed by field id,
 * so null here does not turn the native path off for the screen — it says only
 * that nothing opens at mount and the keypad waits for a tap.
 *
 * Only while the amount is the first field of the form (Decision 21 of
 * docs/plans/entry-lines-and-layout.md). Dragged lower, it is a compact box
 * like any other and the keypad does not open by itself: the same rule
 * `entry-form.tsx` draws the hero by, from the same `formOpeningOf`.
 */
export const nativeAmountFieldId = (schema: RecordSchemaResponse): string | null =>
  formOpeningOf(schema, (field) => field.component === 'money.calculator').heroId;

/*
 * The drawn vocabulary now lives in `@alluzo/shared`, so `packages/ui` can
 * reach it too — `money-calculator.logic.ts` drew money with its own `Intl`
 * call because it had no other option. Re-exported here so every importer of
 * this module is untouched, and so this file stays the one door an entry
 * surface opens.
 */
import {
  formatClock,
  formatDateTime,
  formatDay,
  formatDayShort,
  formatMoney,
  formatNumber,
  formulaOf,
  workedResultOf,
} from '@alluzo/shared';

export {
  formatClock,
  formatDateTime,
  formatDay,
  formatDayLong,
  formatDayRange,
  formatDayShort,
  formatMoney,
  formatNumber,
  formatStamp,
  formatWeekday,
  formatWeekdayDay,
} from '@alluzo/shared';

/**
 * The place a location value names, or null when it names only a point.
 *
 * `map.picker` stores "lat,lng", with an optional ";name" after it — the frozen
 * field contract in packages/ui/src/fields/contract.ts, written by
 * map-picker.logic.ts. A pair of numbers is not a place a person reads, so a
 * row that gets one draws the field's own label instead.
 */
export const placeNameOf = (stored: string): string | null => {
  const [pair, ...rest] = stored.split(';');
  const name = rest.join(';').trim();
  if (name !== '') return name;

  const parts = (pair ?? '').split(',');
  const isPoint =
    parts.length === 2 &&
    parts.every((part) => part.trim() !== '' && Number.isFinite(Number(part)));
  if (isPoint) return null;

  const typed = stored.trim();
  return typed === '' ? null : typed;
};

/** The field that carries a quantity's unit, when the template named one. */
export const unitFieldIdOf = (field: RecordSchemaField): string | null => {
  const named = field.props['unit_slot_field'];
  return typeof named === 'string' && named !== '' ? named : null;
};

/**
 * The unit a quantity wears, or null when it wears none.
 *
 * `number.plain` says a unit is either a field of its own, which the entry
 * chose, or a list. A list of one needs no choosing, so it is the unit. A list
 * of several with nothing chosen is not a unit this row may invent.
 */
export const unitOf = (
  schema: RecordSchemaResponse,
  field: RecordSchemaField,
  values: EntryValues,
  references: Record<string, string>,
): string | null => {
  const fieldId = unitFieldIdOf(field);
  if (fieldId !== null) {
    const word = formatValue(schema, fieldId, values[fieldId] ?? null, references);
    return word === '' ? null : word;
  }
  const list = field.props['unit_list'];
  if (Array.isArray(list) && list.length === 1 && typeof list[0] === 'string') return list[0];
  return null;
};

/**
 * What a computed value wears in front of it and behind it.
 *
 * `value.readonly` declares `prefix_key` and `suffix_key`, so a formula over
 * money prints a currency mark and a rollup of litres prints its unit. Both are
 * catalogue keys, because the template ships them and a shop reads them.
 */
export const affixesOf = (field: RecordSchemaField): { prefix: string; suffix: string } => ({
  prefix: typeof field.props['prefix_key'] === 'string' ? tData(field.props['prefix_key']) : '',
  suffix: typeof field.props['suffix_key'] === 'string' ? tData(field.props['suffix_key']) : '',
});

/**
 * The words a multi select draws, in the order chosen.
 *
 * Over a list (Decision 4 of `dependent-choices-and-multi-select.md`) it holds
 * row ids, named by the titles the list page resolved, as a reference's are;
 * over options, by the record's own option labels. A row or an option nobody
 * can name draws nothing, as a reference's uuid does not.
 */
export const chosenWordsOf = (
  field: RecordSchemaField,
  value: EntryValue,
  references: Record<string, string>,
): string[] => {
  const chosen = Array.isArray(value) ? value.filter((id): id is string => typeof id === 'string') : [];
  if (field.targetRecordId !== null) {
    return chosen.flatMap((id) => {
      const title = references[id] ?? '';
      return title === '' ? [] : [title];
    });
  }
  return chosen.flatMap((id) => {
    const option = field.options?.find((candidate) => candidate.id === id);
    return option === undefined ? [] : [option.label ?? tData(option.labelKey)];
  });
};

/**
 * One value as a line of text.
 *
 * `references` carries the names behind reference ids, because the row holds a
 * uuid and the device cannot look one up. A reference with no resolved name
 * draws nothing rather than drawing the uuid.
 */
export const formatValue = (
  schema: RecordSchemaResponse,
  fieldId: string,
  value: EntryValue,
  references: Record<string, string>,
): string => {
  if (value === null || value === undefined) return '';
  const field = schema.fields.find((candidate) => candidate.id === fieldId);
  if (field === undefined) return '';

  switch (field.type) {
    case 'money':
      return formatMoney(Number(value), schema.currency);
    case 'number':
    case 'quantity':
      return formatNumber(Number(value));
    case 'formula':
    case 'rollup': {
      // A worked-out amount is drawn as the money it is: a picked booking
      // reads "₹13,500" beneath its name, not "13500". Text and yes-or-no
      // answers fall through as they are.
      const result = workedResultOf(field.type, field.spec);
      const figure = Number(value);
      if (result === 'money' && Number.isFinite(figure)) return formatMoney(figure, schema.currency);
      if ((result === 'number' || result === 'quantity') && Number.isFinite(figure)) return formatNumber(figure);
      // A worked-out date (F9) draws as a row's day: 28 Feb, not 2026-02-28.
      if (result === 'date' && typeof value === 'string') return formatDayShort(value);
      return Array.isArray(value) ? value.join(', ') : String(value);
    }
    case 'reference':
      return typeof value === 'string' ? (references[value] ?? '') : '';
    case 'multi_select':
      // Row ids or option ids, never drawn as themselves.
      return chosenWordsOf(field, value, references).join(', ');
    case 'select': {
      // The option carries a key, never a word. This is the layer that turns
      // one into the other, and tData() throws on a key the catalogue lacks.
      const option = field.options?.find((candidate) => candidate.id === value);
      return option === undefined ? '' : (option.label ?? tData(option.labelKey));
    }
    case 'boolean':
      return value === true ? 'Yes' : 'No';
    case 'date':
      return typeof value === 'string' ? formatDayShort(value) : '';
    case 'datetime':
      return typeof value === 'string' ? formatDateTime(value) : '';
    default:
      return Array.isArray(value) ? value.join(', ') : String(value);
  }
};

/** The field types whose stored value a person reads only once it is formatted: a number, or a day. */
const FORMATTED_TYPES: ReadonlySet<string> = new Set(['money', 'number', 'quantity', 'date', 'datetime']);

/**
 * A value Coach proposes, as the record's own rows would draw it, when the
 * server sent no word for it: an amount with its currency and Indian grouping
 * (₹4,250, not 4250), a day as a row's day (26 Sep, not 2026-09-26). Null for
 * any other kind, for a field or a schema this phone does not hold, and for a
 * value that is not one of those, so the card falls back to what it was sent.
 */
export const proposedWordOf = (schema: RecordSchemaResponse | null, fieldId: string, value: unknown): string | null => {
  if (schema === null || value === null || value === undefined) return null;
  const field = schema.fields.find((candidate) => candidate.id === fieldId);
  if (field === undefined || !FORMATTED_TYPES.has(field.type)) return null;
  const isDay = field.type === 'date' || field.type === 'datetime';
  // A number may arrive as a string (pg's numeric); a day only as one.
  const readable = isDay
    ? typeof value === 'string'
    : typeof value === 'number' || (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value)));
  if (!readable) return null;
  const word = formatValue(schema, fieldId, value as EntryValue, {});
  return word === '' ? null : word;
};

/** One field of an entry Coach proposes, as the server sends it. */
export type ProposedField = {
  readonly fieldId: string;
  readonly value: string | number | boolean | readonly string[] | null;
  /** The word the tool resolved: a directory row's title, an option's word. */
  readonly display: string | null;
  /** A multi select's rows by name, for the card's chips. */
  readonly chips?: readonly string[];
  /** The parent row a filtered reference's row is filed under, by name. */
  readonly under?: string;
};

/**
 * A proposed entry's value as its card shows it: the word the tool resolved,
 * else the value as the record's rows draw it (`proposedWordOf`), else the
 * value itself — a list joined, a yes as done and a no as a dash. `schema` is
 * the record's, off the phone's mirror, or null when it does not hold it.
 */
export const proposedValueOf = (schema: RecordSchemaResponse | null, field: ProposedField): string => {
  if (field.display !== null) {
    // A filtered reference names its row with the parent it is filed under:
    // "Electricity, under Utilities", so a shared name ("Other") is never
    // read as the wrong one.
    return field.under === undefined || field.under === '' ? field.display : t('agent.card.under', { value: field.display, parent: field.under });
  }
  const word = proposedWordOf(schema, field.fieldId, field.value);
  if (word !== null) return word;
  const { value } = field;
  if (value === null) return '';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'boolean') return value ? t('common.done') : '—';
  return String(value);
};

/**
 * A multi select's rows as its card draws them: one chip per row, in the
 * order chosen. Null for every other field, and for a card proposed before
 * the chips existed, which says the names joined through `proposedValueOf`.
 */
export const proposedChipsOf = (field: ProposedField): readonly string[] | null =>
  field.chips !== undefined && field.chips.length > 0 ? field.chips : null;

/** The row's headline. Falls back to the first filled text the row carries. */
export const titleOf = (
  schema: RecordSchemaResponse,
  values: EntryValues,
  references: Record<string, string>,
): string => {
  const titleField = fieldFor(schema, 'title');
  if (titleField !== null) {
    const drawn = formatValue(schema, titleField, values[titleField] ?? null, references);
    if (drawn !== '') return drawn;
  }
  for (const field of schema.fields) {
    if (field.type !== 'text' && field.type !== 'long_text') continue;
    const drawn = formatValue(schema, field.id, values[field.id] ?? null, references);
    if (drawn !== '') return drawn;
  }
  return '';
};

/**
 * Whether a record's measure is money (R3 of docs/plans/qa-fixes-2.md).
 *
 * A count or a movement is not: a `quantity` or `number` measure, or a
 * worked-out one whose answer is declared as something other than money.
 * Stock counts cups, and a record carrying the business's currency does not
 * make its cups rupees. Every other measure reads as money, as it always has,
 * and so does a record that measures nothing (it draws no figure to ask about).
 */
export const measureIsMoney = (schema: RecordSchemaResponse): boolean => {
  const fieldId = fieldFor(schema, 'measure');
  const field = fieldId === null ? undefined : schema.fields.find((one) => one.id === fieldId);
  if (field === undefined) return true;
  if (field.type === 'quantity' || field.type === 'number') return false;
  if (field.type === 'formula') {
    const result = formulaOf(field.spec)?.result;
    return result === undefined || result === 'money';
  }
  return true;
};

/**
 * Whether the measure is a count that carries its own direction on the value.
 *
 * A record with no sign binding whose measure is a count: a link that takes
 * stock out writes its quantity negative (the link's own sign, in the server's
 * links.ts), so the row's figure and the record's total are the quantities as
 * stored, summed with their signs, never "Total 0". The same records the
 * server's catalog headline sums this way (MOVEMENT_TOTAL_SQL in its
 * routes/records.ts): a `quantity` or `number` measure and no sign binding.
 */
export const measureIsMovement = (schema: RecordSchemaResponse): boolean => {
  if (schema.bindings.sign !== undefined) return false;
  const fieldId = fieldFor(schema, 'measure');
  const type = fieldId === null ? undefined : schema.fields.find((one) => one.id === fieldId)?.type;
  return type === 'quantity' || type === 'number';
};

/** The currency the measure's figures wear: the record's for money, none for a count. */
export const measureCurrencyOf = (schema: RecordSchemaResponse): string | null =>
  measureIsMoney(schema) ? schema.currency : null;

/**
 * One measure figure as a row, a hero or a total says it.
 *
 * Money is the amount without its sign, in the record's currency: its colour
 * (and its column) says which way it went. A count is never money (R3): it is
 * the number with its own sign, so a quantity taken out reads "-2", with its
 * unit when it has one ("-2 kg").
 */
export const measureWords = (schema: RecordSchemaResponse, value: number, unit: string | null = null): string => {
  if (measureIsMoney(schema)) return formatMoney(Math.abs(value), schema.currency);
  const figure = formatNumber(value);
  return unit === null || unit === '' ? figure : `${figure} ${unit}`;
};

/**
 * The unit a row's measure wears, or null: a count's own unit (`unitOf`), and
 * never one for money, whose currency is its unit.
 */
export const measureUnitOf = (
  schema: RecordSchemaResponse,
  values: EntryValues,
  references: Record<string, string>,
): string | null => {
  if (measureIsMoney(schema)) return null;
  const fieldId = fieldFor(schema, 'measure');
  const field = fieldId === null ? undefined : schema.fields.find((one) => one.id === fieldId);
  return field === undefined ? null : unitOf(schema, field, values, references);
};

/** The measure, unsigned, or null when this record measures nothing. */
export const measureOf = (schema: RecordSchemaResponse, values: EntryValues): number | null => {
  const fieldId = fieldFor(schema, 'measure');
  if (fieldId === null) return null;
  const raw = values[fieldId];
  const parsed = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : Number.NaN;
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Which way this row points: 1, -1, or null when it is unsigned.
 *
 * The same two shapes the server resolves. A constant answers every row; a
 * field answers per row, off the option the entry chose.
 */
export const signOf = (schema: RecordSchemaResponse, values: EntryValues): 1 | -1 | null => {
  const binding = schema.bindings.sign;
  if (binding === undefined) return null;
  if ('constNum' in binding) return binding.constNum;
  const field = schema.fields.find((candidate) => candidate.id === binding.fieldId);
  const chosen = values[binding.fieldId];
  if (field === undefined || typeof chosen !== 'string') return null;
  return field.options?.find((option) => option.id === chosen)?.sign ?? null;
};

/**
 * The calendar day this entry belongs to, as YYYY-MM-DD, or null.
 *
 * A day-only date field stores its own calendar day (`YYYY-MM-DD`), which is
 * that day in any zone. An instant (a `mode: 'datetime'` axis, or the
 * `createdAt` fallback) is read as a day in the workspace's zone (R2 of
 * docs/plans/qa-fixes-2.md), the zone `workspaceToday` asks with: a sale at
 * 01:55 IST on 18 Oct is the 18th, though UTC still calls it the 17th.
 */
export const dayOf = (
  schema: RecordSchemaResponse,
  entry: EntryListItem,
  zone: string | null = workspaceZone(),
): string | null => {
  const fieldId = fieldFor(schema, 'time_axis');
  const raw = fieldId === null ? null : entry.values[fieldId];
  const source = typeof raw === 'string' && raw !== '' ? raw : entry.createdAt;
  if (/^\d{4}-\d{2}-\d{2}$/.test(source)) return source;
  const day = new Date(source);
  if (Number.isNaN(day.getTime())) return null;
  // A day-only field's value written as UTC midnight is still that field's own day.
  const field = source === raw ? schema.fields.find((one) => one.id === fieldId) : undefined;
  const dayOnly = field !== undefined && field.type === 'date' && field.props?.mode !== 'datetime';
  return dayOnly ? day.toISOString().slice(0, 10) : dayInZone(day, zone);
};

export type EntryDay = { day: string; entries: EntryListItem[] };

/**
 * The list, cut into days, newest first.
 *
 * The server already ordered by sequence, so this only groups. It never
 * re-sorts within a day, because the sequence is the truth about what was
 * recorded first and a date field can be backdated.
 */
export const groupByDay = (schema: RecordSchemaResponse, entries: readonly EntryListItem[]): EntryDay[] => {
  const days: EntryDay[] = [];
  for (const entry of entries) {
    const day = dayOf(schema, entry) ?? '';
    const last = days[days.length - 1];
    if (last !== undefined && last.day === day) last.entries.push(entry);
    else days.push({ day, entries: [entry] });
  }
  return days;
};

/**
 * The day an instant (a record's `lastEntryAt`, a row's `createdAt`) falls
 * on in the workspace, as a `YYYY-MM-DD` a heading compares with today. Its
 * first ten characters are the UTC day, a day early from 00:00 to 05:30 IST.
 */
export const workspaceDayOfInstant = (instant: string, zone: string | null = workspaceZone()): string => {
  const at = new Date(instant);
  return Number.isNaN(at.getTime()) ? instant.slice(0, 10) : dayInZone(at, zone);
};

/** The calendar day before a `YYYY-MM-DD` day. */
export const dayBefore = (day: string): string => {
  const at = new Date(`${day}T00:00:00.000Z`);
  at.setUTCDate(at.getUTCDate() - 1);
  return at.toISOString().slice(0, 10);
};

/**
 * A day heading a person reads: Today, Yesterday, or the date.
 *
 * `today` is an instant, read as a day in the workspace's zone (R2 of
 * docs/plans/qa-fixes-2.md): at 01:55 IST on 18 Oct the 18th is Today, though
 * UTC still calls it the 17th.
 */
export const dayLabel = (day: string, today: Date, zone: string | null = workspaceZone()): string => {
  if (day === '') return '';
  const todayDay = dayInZone(today, zone);
  if (day === todayDay) return t('entry.day_today');
  if (day === dayBefore(todayDay)) return t('entry.day_yesterday');
  // The same day a heading anywhere else draws; only the two named days differ.
  return formatDay(day);
};
